import { useState } from "react";
import { apiUrl } from "@api";
import { isMobile } from "react-device-detect";
import { Overlay } from "@components";
import { cn } from "@utils";
import {
  FileEarmarkPdf,
  FileEarmarkWord,
  FileEarmarkImage,
  FileEarmarkText,
  FileEarmarkSpreadsheet,
  FileEarmarkCode,
  FileEarmark,
  Download,
  Eye,
  BoxArrowUpRight,
} from "react-bootstrap-icons";

export const RequestDetails = ({
  id,
  customerName,
  reference,
  files,
  className,
}) => {
  const [filePreview, setFilePreview] = useState(null);

  const PREVIEWABLE_EXTS_MOBILE = new Set([
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "svg",
    "bmp",
    "tif",
    "tiff",
    "txt",
    "csv",
    "xml",
  ]);

  const PREVIEWABLE_EXTS_DESKTOP = new Set([
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "bmp",
    "gif",
  ]);

  const PREVIEWABLE_EXTS = isMobile
    ? PREVIEWABLE_EXTS_MOBILE
    : PREVIEWABLE_EXTS_DESKTOP;

  const pickIcon = (ext) => {
    if (
      [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "bmp",
        "svg",
        "webp",
        "tif",
        "tiff",
      ].includes(ext)
    )
      return FileEarmarkImage;
    if (ext === "pdf") return FileEarmarkPdf;
    if (ext === "doc" || ext === "docx") return FileEarmarkWord;
    if (ext === "csv") return FileEarmarkSpreadsheet;
    if (["xml", "html", "htm"].includes(ext)) return FileEarmarkCode;
    if (["txt", "rtf"].includes(ext)) return FileEarmarkText;
    return FileEarmark;
  };

  const getFileExt = (name) => name.split(".").pop().toLowerCase();

  const baseFileUrl = id ? `${apiUrl()}/api/paymentrequests/${id}/files` : null;
  const previewUrl = (name) => `${baseFileUrl}/${name}?isPreview=true`;
  const downloadUrl = (name) => `${baseFileUrl}/${name}`;

  return (
    <div
      className={cn(
        "py-8 px-5 lg:px-0 border-b border-zinc-200 lg:border-0",
        className
      )}
    >
      <p className="text-zinc-500 mb-1">Name</p>
      <p className="font-medium mb-6">{customerName}</p>
      <p className="text-zinc-500 mb-1">Reference</p>
      <p className="font-medium mb-6">{reference}</p>

      {baseFileUrl && files?.length > 0 && (
        <>
          <p className="text-zinc-500 mb-2">Attachments</p>
          {files.map((name) => {
            const ext = getFileExt(name);
            const canPreview = PREVIEWABLE_EXTS.has(ext);
            const Icon = pickIcon(ext);

            return (
              <div key={name} className="flex items-center mb-2">
                <Icon className="text-zinc-400 w-4 h-4 mr-3" />
                <span>{name}</span>
                <div className="flex items-center ml-auto">
                  {canPreview && (
                    <button
                      className="px-4 py-2 cursor-pointer"
                      onClick={() =>
                        setFilePreview({ name, url: previewUrl(name) })
                      }
                    >
                      <Eye />
                    </button>
                  )}
                  <a
                    href={downloadUrl(name)}
                    download={name}
                    className="px-4 py-2 cursor-pointer"
                  >
                    <Download />
                  </a>
                </div>
              </div>
            );
          })}
        </>
      )}

      <Overlay
        isOpen={!!filePreview}
        title={filePreview?.name}
        onClose={() => setFilePreview(null)}
        action={
          filePreview
            ? {
                icon: <BoxArrowUpRight className="w-4 h-4" />,
                label: "Open in new tab",
                onClick: () =>
                  window.open(filePreview.url, "_blank", "noopener,noreferrer"),
              }
            : undefined
        }
      >
        {filePreview &&
          (() => {
            const ext = getFileExt(filePreview.name);

            if (
              ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)
            )
              return (
                <img
                  src={filePreview.url}
                  className="max-w-full max-h-full object-contain"
                  alt={filePreview.name}
                />
              );

            if (ext === "pdf" && isMobile)
              return (
                <embed
                  src={filePreview.url}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                />
              );

            return (
              <iframe
                src={filePreview.url}
                title={ext === "pdf" ? "PDF preview" : "file"}
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            );
          })()}
      </Overlay>
    </div>
  );
};
